'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { StoreLink, useStoreHref } from '@/components/store';
import type { NavigationItem } from '@cobrastyle/shared-types';
import { cn } from '@cobrastyle/ui';
import { isBrandsCategory } from '@/lib/brands';
import type { CategoryPromoSlot } from '@/lib/category-promo-slots';
import { MegaMenu, type BrandMenu } from './mega-menu';

// Re-export mobile navigation from its separate file
export { MobileNavigation } from './mobile-navigation';

// The brands payload is shaped by the header and consumed by both this menu and
// the search overlay, so it keeps being importable from here.
export type { BrandMenu };

interface NavigationProps {
  items: NavigationItem[];
  /**
   * The brands mega-menu payload, when the header could fetch it. Pre-narrowed on
   * the server: this component is a client component, so shipping all 73 brand
   * categories here to render 8 would put ~10 KB of unused objects in the flight
   * payload of every route. `Category` rather than `NavigationItem` because the
   * latter carries no image or product count.
   *
   * Null means the fetch failed — the brands item then behaves like any other
   * category.
   */
  brandMenu?: BrandMenu | null;
  /**
   * Merchant-authored promo tiles per category (up to 2), keyed by
   * `NavigationItem.id`. Most categories have none — the mega menu falls
   * back to its plain subcategory-list layout for those.
   */
  promoSlotsByCategory?: Record<string, CategoryPromoSlot[]>;
}

// Strip a trailing URL suffix (e.g. ".html") and split into path segments so a
// parent nav item ("vapen") can match the category page you're on, including
// deeper subcategory routes ("vapen/pistoler").
function pathSegments(path: string): string[] {
  return path.replace(/\.[^./]*$/, '').split('/').filter(Boolean);
}

export function Navigation({ items, brandMenu, promoSlotsByCategory }: NavigationProps) {
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const activeNav = items.find((item) => item.id === activeItem);
  const promoSlots = activeNav ? promoSlotsByCategory?.[activeNav.id] : undefined;

  // The brands dropdown promotes a curated set instead of the first 12 children by
  // Magento position (which is alphabetical, so it used to show Aimpoint → Benelli).
  //
  // An empty `brands` falls through to the generic dropdown rather than rendering
  // an empty grid beside a "view all" button: every curated brand can legitimately
  // vanish (renamed or unpublished in Magento), and that must degrade to the
  // ordinary listing, not to a hole.
  const menuBrands =
    activeNav && isBrandsCategory(activeNav.urlKey) && brandMenu?.brands.length
      ? brandMenu
      : null;

  const pathname = usePathname();
  const toHref = useStoreHref();
  const currentSegments = pathSegments(pathname);

  // Active when the item's path is a prefix of the current route — highlights a
  // parent category while browsing any of its subcategories.
  const isCurrent = (urlKey: string): boolean => {
    const itemSegments = pathSegments(toHref(`/${urlKey}`));
    return (
      itemSegments.length > 0 &&
      itemSegments.every((seg, i) => currentSegments[i] === seg)
    );
  };

  return (
    <nav
      className="relative hidden border-t border-border bg-background lg:block"
      onMouseLeave={() => setActiveItem(null)}
      // The panel is a CHILD of this nav, so following a link inside it never
      // fires the `mouseleave` above: Next navigates on the client, the nav
      // never unmounts, and the pointer is still inside the nav tree when the
      // new route paints — leaving the menu lying over the products it just
      // loaded, with no way out but a click somewhere else on the page.
      //
      // A click anywhere in here closes it, which also covers the one case a
      // route-change effect cannot: following the category you are already on,
      // where the pathname never changes. The mobile drawer solves the same
      // problem with an `onNavigate` threaded to every row — one handler is the
      // smaller version of that, because this panel has no state to keep.
      onClick={() => setActiveItem(null)}
    >
      <div className="container mx-auto">
        {/* Left-aligned to the content column (`-ml-3.5` cancels the first link's
            px-3.5). Scrolls horizontally when there are more categories than fit,
            so every item keeps its mega-menu. Scrollbar hidden for a clean look. */}
        <ul className="-ml-3.5 flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((item) => {
            const current = isCurrent(item.urlKey);
            return (
            <li
              key={item.id}
              className="shrink-0"
              onMouseEnter={() => setActiveItem(item.id)}
            >
              <StoreLink
                href={`/${item.urlKey}`}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  // The active marker is a bottom border on the whole item (like
                  // the PDP tabs) rather than a text underline, so the line sits
                  // on the nav's bottom edge and spans the item's full width. The
                  // transparent border is always there to keep every item the same
                  // height whether or not it's current, and shows on hover too
                  // (menu open), not just for the current route. `flex` (not just
                  // padding) is required here: padding on an inline element doesn't
                  // add to the row's height, so without it the bar collapses thin.
                  // The canvas marks an active item with `var(--accent)`, not
                  // ink — the olive rule is the one warm thing in the chrome and
                  // it ties the bar to the prices and links below it.
                  // `duration-base` is the theme's 200ms: the default 150ms
                  // reads as a snap on a band this wide.
                  'flex items-center whitespace-nowrap border-b-2 border-transparent px-3.5 py-3.5 text-[13.5px] font-semibold uppercase leading-[18px] tracking-[0.04em] text-muted-foreground transition-colors duration-base ease-out hover:text-foreground',
                  // Pale sage rather than the canvas's grey `--panel-deep`: at
                  // this size a neutral block reads as a lit-up slab, where the
                  // tint reads as the item warming up.
                  activeItem === item.id && 'border-accent-600 bg-accent-50 text-foreground',
                  current && 'border-accent-600 text-foreground'
                )}
              >
                {item.name}
              </StoreLink>
            </li>
            );
          })}
        </ul>
      </div>

      {/* Keyed on the item so moving to another category remounts the panel
          rather than reconciling one category's rows into another's. */}
      {activeNav && activeNav.children.length > 0 && (
        <MegaMenu
          key={activeNav.id}
          item={activeNav}
          menuBrands={menuBrands}
          promoSlots={promoSlots}
        />
      )}
    </nav>
  );
}
